/**
 * Revenue Tax setup (PIS/COFINS provision with the revenue) — shared codes, copy and API shapes.
 * ADR 0024, epic ALC-4408, slice ALC-4410.
 *
 * One status read, one call to confirm the Revenue Classes, one call to map the accounts, then the
 * due basis and the start month. A gate refuses the start month until the setup is complete and
 * names what is missing. Every stable code below has en and pt-BR copy; callers classify by CODE,
 * never by message text.
 */
import type {
  CompanyTaxRegime,
  PisCofinsRegime,
  RevenueClass,
  RevenueTaxOutcome,
  RevenueTaxRuleSource,
  TaxDueBasis,
} from './generated/enums.js';
import {
  REVENUE_TAX_ERROR_MESSAGES,
  type RevenueTaxCopy,
  RevenueTaxErrorCode,
  type RevenueTaxSlotCode,
} from './revenue-tax.js';

export type RevenueTaxSetupLocale = 'en' | 'ptBR';

/** Business names of the seven Revenue Classes (pt-BR is the product language). */
export const REVENUE_CLASS_LABELS: Readonly<Record<RevenueClass, RevenueTaxCopy>> = Object.freeze({
  OPERATING: { ptBR: 'Receita operacional', en: 'Operating revenue' },
  OPERATING_CUMULATIVE: {
    ptBR: 'Receita operacional (sempre cumulativa)',
    en: 'Operating revenue (always cumulative)',
  },
  FINANCIAL: { ptBR: 'Receita financeira', en: 'Financial revenue' },
  FINANCIAL_ZERO_RATED: {
    ptBR: 'Receita financeira com alíquota zero (variação cambial de exportação/passivo)',
    en: 'Zero-rated financial revenue (FX variation on exports/liabilities)',
  },
  EQUITY_INTEREST: { ptBR: 'Juros sobre capital próprio', en: 'Interest on equity (JCP)' },
  EXPORT: { ptBR: 'Receita de exportação', en: 'Export revenue' },
  NOT_TAXED: { ptBR: 'Receita fora da base', en: 'Revenue outside the PIS/COFINS base' },
});

/** Business names of the accounts Revenue Tax needs mapped, by slot code. */
export const REVENUE_TAX_SLOT_LABELS: Readonly<Record<string, RevenueTaxCopy>> = Object.freeze({
  DEDUCAO_PIS: { ptBR: 'Dedução da receita - PIS', en: 'Revenue deduction - PIS' },
  DEDUCAO_COFINS: { ptBR: 'Dedução da receita - COFINS', en: 'Revenue deduction - COFINS' },
  PIS_FATURAMENTO: { ptBR: 'PIS sobre faturamento a recolher', en: 'PIS on revenue payable' },
  COFINS_FATURAMENTO: {
    ptBR: 'COFINS sobre faturamento a recolher',
    en: 'COFINS on revenue payable',
  },
  PIS_FATURAMENTO_A_DIFERIR: {
    ptBR: 'PIS sobre faturamento a diferir (regime de caixa)',
    en: 'PIS on revenue to defer (cash basis)',
  },
  COFINS_FATURAMENTO_A_DIFERIR: {
    ptBR: 'COFINS sobre faturamento a diferir (regime de caixa)',
    en: 'COFINS on revenue to defer (cash basis)',
  },
  PIS_SOBRE_RECEITA_DIFERIDA: {
    ptBR: 'PIS sobre receita diferida (regime de caixa)',
    en: 'PIS on deferred revenue (cash basis)',
  },
  COFINS_SOBRE_RECEITA_DIFERIDA: {
    ptBR: 'COFINS sobre receita diferida (regime de caixa)',
    en: 'COFINS on deferred revenue (cash basis)',
  },
});

// ---------------------------------------------------------------------------------------------
// Stable codes
// ---------------------------------------------------------------------------------------------

/** Codes the status lists as blockers (and the gate repeats inside REVENUE_TAX_SETUP_INCOMPLETE). */
export const RevenueTaxSetupBlockerCode = {
  /** One or more postable revenue accounts have no Revenue Class. */
  REVENUE_ACCOUNTS_WITHOUT_CLASS: 'REVENUE_ACCOUNTS_WITHOUT_CLASS',
  /** A required slot (deduction or tax rail, plus the cash-basis slots when CASH) has no account. */
  REVENUE_TAX_ACCOUNT_NOT_MAPPED: 'REVENUE_TAX_ACCOUNT_NOT_MAPPED',
  /** The PIS / COFINS payable slot is mapped but its Apurado / Em guias rail does not exist yet. */
  REVENUE_TAX_RAIL_NOT_ENSURED: 'REVENUE_TAX_RAIL_NOT_ENSURED',
  /** Simples Nacional, no fiscal config, or an entity outside Brazil. */
  REVENUE_TAX_NOT_APPLICABLE: 'REVENUE_TAX_NOT_APPLICABLE',
  /** The entity has no book or no active chart of accounts yet. */
  REVENUE_TAX_NO_ACTIVE_CHART: 'REVENUE_TAX_NO_ACTIVE_CHART',
} as const;
export type RevenueTaxSetupBlockerCode =
  (typeof RevenueTaxSetupBlockerCode)[keyof typeof RevenueTaxSetupBlockerCode];

/** Codes the setup API THROWS (HTTP 4xx). `REVENUE_TAX_NOT_APPLICABLE` is shared with the blockers. */
export const RevenueTaxSetupErrorCode = {
  REVENUE_TAX_SETUP_INCOMPLETE: 'REVENUE_TAX_SETUP_INCOMPLETE',
  REVENUE_TAX_NOT_APPLICABLE: 'REVENUE_TAX_NOT_APPLICABLE',
  REVENUE_TAX_CASH_BASIS_NOT_ALLOWED: 'REVENUE_TAX_CASH_BASIS_NOT_ALLOWED',
  /**
   * Owner decision H5 (ALC-4414): the cash basis does not cover advance-billed contracts yet (their
   * revenue reaches the ledger through Contract Liability, ALC-4419). Refused when the entity elects
   * CASH with such a contract, and when such a contract is activated / amended on a CASH entity.
   */
  REVENUE_TAX_CASH_BASIS_ADVANCE_UNSUPPORTED: 'REVENUE_TAX_CASH_BASIS_ADVANCE_UNSUPPORTED',
  /** Lucro Presumido with the non-cumulative PIS/COFINS regime (Lei 10.637/2002 art. 8 II). */
  REVENUE_TAX_REGIME_COMBINATION_INVALID: 'REVENUE_TAX_REGIME_COMBINATION_INVALID',
  /** Switch-on refused: the status lists warnings that need `acknowledgeWarnings: true`. */
  REVENUE_TAX_WARNINGS_NOT_ACKNOWLEDGED: 'REVENUE_TAX_WARNINGS_NOT_ACKNOWLEDGED',
  REVENUE_TAX_START_MONTH_INVALID: 'REVENUE_TAX_START_MONTH_INVALID',
  REVENUE_TAX_START_MONTH_HAS_PROVISIONS: 'REVENUE_TAX_START_MONTH_HAS_PROVISIONS',
  REVENUE_TAX_START_MONTH_LOCKED: 'REVENUE_TAX_START_MONTH_LOCKED',
  REVENUE_TAX_DUE_BASIS_LOCKED: 'REVENUE_TAX_DUE_BASIS_LOCKED',
  REVENUE_CLASS_REQUIRED: 'REVENUE_CLASS_REQUIRED',
  REVENUE_CLASS_ACCOUNT_NOT_REVENUE: 'REVENUE_CLASS_ACCOUNT_NOT_REVENUE',
  REVENUE_CLASS_ACCOUNT_NOT_FOUND: 'REVENUE_CLASS_ACCOUNT_NOT_FOUND',
  REVENUE_CLASS_DUPLICATE_ACCOUNT: 'REVENUE_CLASS_DUPLICATE_ACCOUNT',
  REVENUE_CLASS_REQUEST_EMPTY: 'REVENUE_CLASS_REQUEST_EMPTY',
  REVENUE_TAX_RATE_OUT_OF_RANGE: 'REVENUE_TAX_RATE_OUT_OF_RANGE',
  REVENUE_TAX_RULE_LEGAL_BASIS_REQUIRED: 'REVENUE_TAX_RULE_LEGAL_BASIS_REQUIRED',
  /** S1's code (`RevenueTaxErrorCode`): one copy, shared with the engine and the resolver. */
  REVENUE_TAX_RULE_INVALID: RevenueTaxErrorCode.REVENUE_TAX_RULE_INVALID,
  REVENUE_TAX_RULE_PERIOD_INVALID: 'REVENUE_TAX_RULE_PERIOD_INVALID',
  REVENUE_TAX_RULE_NOT_FOUND: 'REVENUE_TAX_RULE_NOT_FOUND',
  REVENUE_TAX_RULE_ENTITY_NOT_FOUND: 'REVENUE_TAX_RULE_ENTITY_NOT_FOUND',
  /**
   * A rule names an entity whose country is not the rule's country (I58), or the entities of one
   * create call are in different countries. Params: `{ entityId, expected, received }` (plus
   * `entityName` for the person).
   */
  REVENUE_TAX_RULE_COUNTRY_MISMATCH: 'REVENUE_TAX_RULE_COUNTRY_MISMATCH',
  /** S1's code (`RevenueTaxErrorCode`): one copy, shared with the engine and the resolver. */
  REVENUE_TAX_RULE_OVERLAP: RevenueTaxErrorCode.REVENUE_TAX_RULE_OVERLAP,
} as const;
export type RevenueTaxSetupErrorCode =
  (typeof RevenueTaxSetupErrorCode)[keyof typeof RevenueTaxSetupErrorCode];

/** Non-blocking codes returned in `warnings` of a successful response. */
export const RevenueTaxSetupWarningCode = {
  /** The account already has posted provisions; the new class applies to future revenue only. */
  REVENUE_CLASS_CHANGED_WITH_POSTED_PROVISIONS: 'REVENUE_CLASS_CHANGED_WITH_POSTED_PROVISIONS',
  /** The chart is shared with other entities: they see the new class too. */
  REVENUE_CLASS_SHARED_CHART: 'REVENUE_CLASS_SHARED_CHART',
  /**
   * Status warning that also refuses the start month: Lucro Presumido cannot be non-cumulative
   * (Lei 10.637/2002 art. 8 II). Shared with the thrown error code of the same name.
   */
  REVENUE_TAX_REGIME_COMBINATION_INVALID: 'REVENUE_TAX_REGIME_COMBINATION_INVALID',
  /**
   * Status warning: a PIS/COFINS guide of the entity is set to CLASSIFY, so paying the DARF would
   * expense the tax a second time on top of the provision. Needs consent to switch on.
   */
  REVENUE_TAX_GUIDE_CLASSIFY_OVERRIDE: 'REVENUE_TAX_GUIDE_CLASSIFY_OVERRIDE',
  /**
   * A change of the company tax regime flips the EFFECTIVE PIS/COFINS due basis (cash only counts
   * under Lucro Presumido) while provisions already reached the ledger. Needs consent
   * (`acknowledgeWarnings: true`); amounts already deferred stay deferred until they are received.
   */
  REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME: 'REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME',
  /**
   * A change of the company tax regime or of the PIS/COFINS regime while provisions already
   * reached the ledger (decision I21). The engine reads the regime saved at the moment of each
   * posting: posted provisions do not change, and revenue dated before the change but posted after
   * it takes the new rates. Needs consent (`acknowledgeWarnings: true`); returned together with
   * `REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME` when the effective basis flips too.
   */
  REVENUE_TAX_REGIME_CHANGES_WITH_POSTED_PROVISIONS:
    'REVENUE_TAX_REGIME_CHANGES_WITH_POSTED_PROVISIONS',
} as const;
export type RevenueTaxSetupWarningCode =
  (typeof RevenueTaxSetupWarningCode)[keyof typeof RevenueTaxSetupWarningCode];

/** Why `ensure-accounts` could not create the account of a slot (the slot is NOT_RESOLVED). */
export const RevenueTaxEnsureReasonCode = {
  /** No mapped anchor account to create the new account next to: map the slot by hand. */
  NO_ANCHOR_ACCOUNT: 'NO_ANCHOR_ACCOUNT',
  /**
   * A chart version that is not the entity's current one (pinned by older periods, or a branch)
   * cannot be set up automatically. The text names the earliest start month the current chart
   * version allows.
   */
  REVENUE_TAX_CHART_VERSION_NOT_READY: 'REVENUE_TAX_CHART_VERSION_NOT_READY',
} as const;
export type RevenueTaxEnsureReasonCode =
  (typeof RevenueTaxEnsureReasonCode)[keyof typeof RevenueTaxEnsureReasonCode];

/**
 * Why a suggestion needs a person to confirm it account by account. A suggestion that LOWERS the
 * tax (anything but OPERATING) or that came only from an ancestor's name is never applied by
 * "confirm all suggestions" and never stamped by the chart import (orchestrator decision I7).
 */
export const RevenueClassExplicitConfirmationReason = {
  /** The suggested class is not OPERATING: it reduces or removes PIS/COFINS. */
  SUGGESTED_CLASS_NOT_OPERATING: 'SUGGESTED_CLASS_NOT_OPERATING',
  /** The keyword matched an ancestor's name, not the account's own name. */
  MATCHED_ON_ANCESTOR_NAME_ONLY: 'MATCHED_ON_ANCESTOR_NAME_ONLY',
} as const;
export type RevenueClassExplicitConfirmationReason =
  (typeof RevenueClassExplicitConfirmationReason)[keyof typeof RevenueClassExplicitConfirmationReason];

export const RevenueTaxNotApplicableReason = {
  SIMPLES_NACIONAL: 'SIMPLES_NACIONAL',
  NO_FISCAL_CONFIG: 'NO_FISCAL_CONFIG',
  NOT_BRAZIL: 'NOT_BRAZIL',
} as const;
export type RevenueTaxNotApplicableReason =
  (typeof RevenueTaxNotApplicableReason)[keyof typeof RevenueTaxNotApplicableReason];

/** Why the suggestion function picked a class. */
export const RevenueClassSuggestionReason = {
  KEYWORD_EQUITY_INTEREST: 'KEYWORD_EQUITY_INTEREST',
  KEYWORD_NOT_TAXED: 'KEYWORD_NOT_TAXED',
  KEYWORD_FX_VARIATION: 'KEYWORD_FX_VARIATION',
  KEYWORD_FINANCIAL: 'KEYWORD_FINANCIAL',
  KEYWORD_EXPORT: 'KEYWORD_EXPORT',
  /** No keyword matched: ordinary operating revenue. */
  DEFAULT_OPERATING: 'DEFAULT_OPERATING',
} as const;
export type RevenueClassSuggestionReason =
  (typeof RevenueClassSuggestionReason)[keyof typeof RevenueClassSuggestionReason];

/** Review flag on an operating suggestion in a NON_CUMULATIVE entity (Lei 10.833/2003 art. 10). */
export const REVENUE_CLASS_REVIEW_REASON_OPERATING_MAY_STAY_CUMULATIVE =
  'OPERATING_MAY_STAY_CUMULATIVE' as const;

export type RevenueClassReviewReason =
  typeof REVENUE_CLASS_REVIEW_REASON_OPERATING_MAY_STAY_CUMULATIVE;

/** The only tax rates above this fraction are rejected as "typed a percent" (25 % is far above any PIS/COFINS rate). */
export const REVENUE_TAX_RATE_MAX_PLAUSIBLE_FRACTION = '0.25';

// ---------------------------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------------------------

export const REVENUE_TAX_SETUP_NOT_APPLICABLE_COPY: Readonly<
  Record<RevenueTaxNotApplicableReason, RevenueTaxCopy>
> = Object.freeze({
  SIMPLES_NACIONAL: {
    ptBR: 'Os tributos sobre a receita não se aplicam ao Simples Nacional: PIS e COFINS já estão dentro do DAS.',
    en: 'Revenue Tax does not apply to Simples Nacional: PIS and COFINS are already inside the DAS.',
  },
  NO_FISCAL_CONFIG: {
    ptBR: 'Configure primeiro o regime tributário da entidade (configuração fiscal) para usar os tributos sobre a receita.',
    en: 'Set the entity tax regime first (entity fiscal settings) to use Revenue Tax.',
  },
  NOT_BRAZIL: {
    ptBR: 'Os tributos sobre a receita (PIS/COFINS) só existem para entidades domiciliadas no Brasil.',
    en: 'Revenue Tax (PIS/COFINS) only exists for entities domiciled in Brazil.',
  },
});

/** One sentence per suggestion reason, for the screen and the MCP client (why this class was suggested). */
export const REVENUE_CLASS_SUGGESTION_REASON_COPY: Readonly<
  Record<RevenueClassSuggestionReason, RevenueTaxCopy>
> = Object.freeze({
  KEYWORD_EQUITY_INTEREST: {
    ptBR: 'O nome fala de juros sobre capital próprio: não é receita operacional.',
    en: 'The name mentions interest on equity: it is not operating revenue.',
  },
  KEYWORD_NOT_TAXED: {
    ptBR: 'O nome indica receita fora da base de cálculo (equivalência patrimonial, dividendo, reversão de provisão, recuperação de crédito baixado ou ganho na venda de ativo não circulante).',
    en: 'The name points to revenue outside the tax base (equity method, dividend, provision reversal, recovery of a written-off credit or gain on the sale of a non-current asset).',
  },
  KEYWORD_FX_VARIATION: {
    ptBR: 'O nome indica variação cambial: receita financeira (alíquota zero apenas se ligada a exportação ou a passivo). Confirme a classe.',
    en: 'The name points to foreign exchange variation: financial revenue (zero-rated only when tied to exports or to a liability). Confirm the class.',
  },
  KEYWORD_FINANCIAL: {
    ptBR: 'O nome indica receita financeira (juros, rendimento ou aplicação).',
    en: 'The name points to financial revenue (interest, yield or investment).',
  },
  KEYWORD_EXPORT: {
    ptBR: 'O nome indica exportação de serviços ou receita do exterior.',
    en: 'The name points to exported services or revenue from abroad.',
  },
  DEFAULT_OPERATING: {
    ptBR: 'Nenhuma palavra especial no nome: receita operacional comum.',
    en: 'No special word in the name: ordinary operating revenue.',
  },
});

/** The question the screen asks on an operating suggestion in a NON_CUMULATIVE entity. */
export const REVENUE_CLASS_REVIEW_REASON_COPY: Readonly<
  Record<RevenueClassReviewReason, RevenueTaxCopy>
> = Object.freeze({
  OPERATING_MAY_STAY_CUMULATIVE: {
    ptBR: 'Software próprio de empresa de TI, educação ou saúde? Essas receitas continuam a 0,65 % de PIS e 3 % de COFINS (Lei 10.833/2003, art. 10). Revenda e software importado não entram. Se for o caso, escolha "Receita operacional (sempre cumulativa)".',
    en: 'Own software of an IT company, education or health service? These stay at 0.65 % PIS and 3 % COFINS (Lei 10.833/2003, art. 10). Resale and imported software do not qualify. If so, choose "Operating revenue (always cumulative)".',
  },
});

export const REVENUE_CLASS_EXPLICIT_CONFIRMATION_REASON_COPY: Readonly<
  Record<RevenueClassExplicitConfirmationReason, RevenueTaxCopy>
> = Object.freeze({
  SUGGESTED_CLASS_NOT_OPERATING: {
    ptBR: 'A classe sugerida reduz ou zera o PIS/COFINS desta conta. "Confirmar todas as sugestões" não a aplica: informe a classe desta conta de forma explícita.',
    en: 'The suggested class lowers or removes PIS/COFINS on this account. "Confirm all suggestions" does not apply it: set this account class explicitly.',
  },
  MATCHED_ON_ANCESTOR_NAME_ONLY: {
    ptBR: 'A sugestão veio apenas do nome de um grupo acima da conta, não do nome dela. "Confirmar todas as sugestões" não a aplica: informe a classe desta conta de forma explícita.',
    en: 'The suggestion came only from the name of a group above the account, not from its own name. "Confirm all suggestions" does not apply it: set this account class explicitly.',
  },
});

/** Copy of the reasons `ensure-accounts` reports on a slot it could not create. */
const REVENUE_TAX_CHART_VERSION_NOT_READY_COPY: RevenueTaxCopy = {
  ptBR: 'O plano de contas {versionLabel} não pôde ser preparado automaticamente (classes e contas fiscais). O primeiro mês de início que o plano de contas atual permite é {months}. Prepare esse plano de contas manualmente ou escolha um mês de início posterior.',
  en: 'Chart version {versionLabel} could not be set up automatically (classes and tax accounts). The earliest start month the current chart version allows is {months}. Set that version up by hand or choose a later start month.',
};
/**
 * The result of `ensure-accounts` carries no months for a chart version, so the screen renders
 * the reason with the version name only. The sentence never leaves a hole where the month goes.
 */
const REVENUE_TAX_CHART_VERSION_NOT_READY_NO_MONTH_COPY: RevenueTaxCopy = {
  ptBR: 'Versão do plano de contas ainda não publicada para o mês de início. O plano de contas {versionLabel} não pôde ser preparado automaticamente (classes e contas fiscais). Prepare esse plano de contas manualmente.',
  en: 'Chart version not yet published for the start month. Chart version {versionLabel} could not be set up automatically (classes and tax accounts). Set that version up by hand.',
};

/**
 * `REVENUE_TAX_RULE_INVALID` has one stable code for several request mistakes. Its default copy is
 * about the rate; a `reason` param selects the sentence of the other mistakes (S9, ALC-4430).
 */
export const REVENUE_TAX_RULE_INVALID_REASON_COPY: Readonly<Record<string, RevenueTaxCopy>> = {
  /**
   * An organization rule has no country: it is never guessed (I58). The copy is for the person;
   * the field an API client has to send is in the error `params` (`field: 'countryCode'`).
   */
  COUNTRY_REQUIRED: {
    ptBR: 'Informe o país da regra: uma regra da organização vale só para as entidades desse país. Nada foi salvo.',
    en: 'Choose the rule country: an organization rule only applies to the entities of that country. Nothing was saved.',
  },
  /**
   * The entity was named twice (the `entityId` query and the body `entityIds`). The fields are in
   * the error `params` (`fields: ['entityId', 'entityIds']`).
   */
  ENTITY_SCOPE_CONFLICT: {
    ptBR: 'A regra veio com as entidades em dois lugares. Escolha as entidades em um só lugar. Nada foi salvo.',
    en: 'The rule came with the entities in two places. Choose the entities in one place only. Nothing was saved.',
  },
  /** "Encerrar" can only bring the end date forward or keep it: a later date would extend the rule (D3). */
  END_DATE_AFTER_CURRENT: {
    ptBR: 'Uma regra só pode ser encerrada até a data de fim atual ({validUntil}); não dá para estendê-la. Para valer por mais tempo, crie uma nova regra.',
    en: 'A rule can only be ended on or before its current end date ({validUntil}); it cannot be extended. To keep it going, create a new rule.',
  },
  /** The country of the rule is not in the country catalog. */
  COUNTRY_UNKNOWN: {
    ptBR: 'O país da regra não existe no cadastro de países. Use o código de duas letras (por exemplo "BR" ou "US"). Nada foi salvo.',
    en: 'The rule country is not in the country catalog. Use the two-letter code (for example "BR" or "US"). Nothing was saved.',
  },
  /** `entityIds` is empty, repeated or has an invalid id. */
  ENTITY_IDS_INVALID: {
    ptBR: 'A lista de entidades da regra está vazia ou repete uma entidade. Escolha ao menos uma entidade, sem repetir. Nada foi salvo.',
    en: 'The rule entity list is empty or repeats an entity. Pick at least one entity, without repeats. Nothing was saved.',
  },
};

/**
 * The overlap sentence when the refusal names the rule it clashes with (`validFrom`): who already
 * has a rule, since when, and what to do. `{who}` is the entity name, or "the organization".
 */
const REVENUE_TAX_RULE_OVERLAP_WITH_START_COPY: RevenueTaxCopy = {
  ptBR: '{who} já tem uma regra para esta classe e tributo valendo desde {validFrom}. Encerre a regra existente ou mude a data "Válida de". Nada foi salvo.',
  en: '{who} already has a rule for this class and tax in force since {validFrom}. End the existing rule or change the "Valid from" date. Nothing was saved.',
};

const ORGANIZATION_WORDS: Readonly<Record<RevenueTaxSetupLocale, string>> = {
  ptBR: 'A organização',
  en: 'The organization',
};

/** `2027-01-01` as `01/01/2027` for pt-BR; the ISO day stays as is in English. Others unchanged. */
function formatDayParam(value: unknown, locale: RevenueTaxSetupLocale): string {
  const text = String(value ?? '');
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match || locale !== 'ptBR') return text;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** The same reason with neither a month nor a chart version name: the plain sentence alone. */
const REVENUE_TAX_CHART_VERSION_NOT_READY_NO_PARAMS_COPY: RevenueTaxCopy = {
  ptBR: 'Versão do plano de contas ainda não publicada para o mês de início.',
  en: 'Chart version not yet published for the start month.',
};

export const REVENUE_TAX_SETUP_MESSAGES: Readonly<
  Record<
    | RevenueTaxSetupBlockerCode
    | RevenueTaxSetupErrorCode
    | RevenueTaxSetupWarningCode
    | RevenueTaxEnsureReasonCode,
    RevenueTaxCopy
  >
> = Object.freeze({
  // Reasons of `ensure-accounts`.
  NO_ANCHOR_ACCOUNT: {
    ptBR: 'Não há conta já mapeada ao lado da qual criar a conta "{slot}". Mapeie a conta em Mapeamento de contas (aba Fiscal).',
    en: 'There is no mapped account to create the "{slot}" account next to. Map the account on Account mapping (Fiscal tab).',
  },
  REVENUE_TAX_CHART_VERSION_NOT_READY: REVENUE_TAX_CHART_VERSION_NOT_READY_COPY,
  // Blockers.
  REVENUE_ACCOUNTS_WITHOUT_CLASS: {
    ptBR: '{count} conta(s) de receita sem Classe de receita: {accounts}. Defina a classe de cada uma ou confirme as sugestões.',
    en: '{count} revenue account(s) have no Revenue Class: {accounts}. Set a class on each one or confirm the suggestions.',
  },
  REVENUE_TAX_ACCOUNT_NOT_MAPPED: {
    ptBR: 'A conta "{slot}" não está mapeada. Mapeie a conta em Mapeamento de contas (aba Fiscal).',
    en: 'The "{slot}" account is not mapped. Map the account on Account mapping (Fiscal tab).',
  },
  REVENUE_TAX_RAIL_NOT_ENSURED: {
    ptBR: 'A conta "{slot}" está mapeada, mas a estrutura Apurado / Em guias ainda não foi criada. Mapeie a conta em Mapeamento de contas (aba Fiscal).',
    en: 'The "{slot}" account is mapped, but its Apurado / guides structure has not been created yet. Map the account on Account mapping (Fiscal tab).',
  },
  REVENUE_TAX_NO_ACTIVE_CHART: {
    ptBR: 'A entidade ainda não tem um plano de contas ativo. Atribua um plano de contas à entidade antes de configurar os tributos sobre a receita.',
    en: 'The entity has no active chart of accounts yet. Assign a chart to the entity before setting up Revenue Tax.',
  },
  // Errors.
  REVENUE_TAX_SETUP_INCOMPLETE: {
    ptBR: 'A configuração dos tributos sobre a receita está incompleta: o mês de início não foi salvo. Resolva os itens pendentes e tente de novo.',
    en: 'The Revenue Tax setup is incomplete: the start month was not saved. Resolve the pending items and try again.',
  },
  REVENUE_TAX_NOT_APPLICABLE: {
    ptBR: '{reason}',
    en: '{reason}',
  },
  REVENUE_TAX_CASH_BASIS_NOT_ALLOWED: {
    ptBR: 'Somente empresa no Lucro Presumido pode usar o regime de caixa para PIS/COFINS, e apenas se também usar o regime de caixa para IRPJ/CSLL.',
    en: 'Only a Lucro Presumido company can use the cash basis for PIS/COFINS, and only if it also uses the cash basis for IRPJ/CSLL.',
  },
  REVENUE_TAX_CASH_BASIS_ADVANCE_UNSUPPORTED: {
    ptBR: 'O regime de caixa ainda não cobre contratos faturados antecipadamente.',
    en: 'The cash basis does not cover advance-billed contracts yet.',
  },
  REVENUE_TAX_REGIME_COMBINATION_INVALID: {
    ptBR: 'Empresa no Lucro Presumido não pode estar no regime não cumulativo de PIS/COFINS (Lei 10.637/2002, art. 8º, II). Ajuste o regime de PIS/COFINS da entidade para cumulativo antes de usar os tributos sobre a receita.',
    en: 'A Lucro Presumido company cannot be on the non-cumulative PIS/COFINS regime (Lei 10.637/2002 art. 8 II). Set the entity PIS/COFINS regime to cumulative before using Revenue Tax.',
  },
  REVENUE_TAX_GUIDE_CLASSIFY_OVERRIDE: {
    ptBR: 'A guia de {slotCodes} desta empresa está com o tratamento "Classificar". Com os tributos sobre a receita ligados, a provisão já reconhece o imposto junto com a receita e pagar a guia o lançaria como despesa de novo (contagem em dobro). Mude o tratamento da guia para "Usar passivo de origem" em Tratamento de guias de impostos, ou confirme que aceita o risco.',
    en: 'The {slotCodes} guide of this company is set to "Classify". With Revenue Tax on, the provision already recognizes the tax with the revenue, and paying the guide would expense it again (double count). Change the guide treatment to "Use source liability" in Tax slip treatment, or confirm that you accept the risk.',
  },
  REVENUE_TAX_WARNINGS_NOT_ACKNOWLEDGED: {
    ptBR: 'Há avisos que precisam de confirmação antes de salvar esta alteração dos tributos sobre a receita. Leia os avisos e confirme para salvar. Nada foi salvo.',
    en: 'There are warnings that need your confirmation before this Revenue Tax change is saved. Read the warnings and confirm to save. Nothing was saved.',
  },
  REVENUE_TAX_START_MONTH_INVALID: {
    ptBR: 'O mês de início dos tributos sobre a receita deve ser o primeiro dia de um mês (por exemplo, 2026-08-01).',
    en: 'The Revenue Tax start month must be the first day of a month (for example, 2026-08-01).',
  },
  REVENUE_TAX_START_MONTH_HAS_PROVISIONS: {
    ptBR: 'Não é possível adiar ou limpar o mês de início: já existem provisões lançadas em {months}. Antecipar o mês de início é permitido.',
    en: 'The start month cannot move later or be cleared: provisions are already posted in {months}. Moving it earlier is allowed.',
  },
  REVENUE_TAX_START_MONTH_LOCKED: {
    ptBR: 'O mês de início dos tributos sobre a receita já está ativo em {currentStart}. Para preservar as apurações mensais, ele não pode ser adiado nem removido; somente pode ser antecipado.',
    en: 'Revenue Tax has already been active since {currentStart}. To preserve monthly assessments, its start month cannot move later or be cleared; it can only move earlier.',
  },
  REVENUE_TAX_DUE_BASIS_LOCKED: {
    ptBR: 'O regime de reconhecimento das receitas (competência ou caixa) não pode mudar depois que há provisões: a opção vale para o ano inteiro e a mudança exige a apuração mensal.',
    en: 'The revenue recognition basis (accrual or cash) cannot change once provisions exist: the election is per year and changing it needs the monthly assessment.',
  },
  REVENUE_CLASS_REQUIRED: {
    ptBR: 'Esta conta de receita precisa de uma Classe de receita: a empresa já usa os tributos sobre a receita (início em {startMonth}). Escolha a classe da conta, por exemplo "Receita operacional". Se for uma conta de dedução, marque-a como redutora.',
    en: 'This revenue account needs a Revenue Class: the company already uses Revenue Tax (started {startMonth}). Choose the account class, for example "Operating revenue". If it is a deduction account, mark it as a contra account.',
  },
  REVENUE_CLASS_ACCOUNT_NOT_REVENUE: {
    ptBR: 'A conta {account} não é uma conta de receita lançável do plano de contas ativo; contas redutoras, de dedução, sintéticas e de outros tipos não recebem Classe de receita. Nada foi salvo.',
    en: 'Account {account} is not a postable revenue account of the active chart; contra, deduction, group and other-type accounts do not take a Revenue Class. Nothing was saved.',
  },
  REVENUE_CLASS_ACCOUNT_NOT_FOUND: {
    ptBR: 'A conta {account} não existe no plano de contas ativo da entidade. Nada foi salvo.',
    en: 'Account {account} is not in the entity active chart. Nothing was saved.',
  },
  REVENUE_CLASS_DUPLICATE_ACCOUNT: {
    ptBR: 'A conta {account} aparece mais de uma vez na lista. Nada foi salvo.',
    en: 'Account {account} appears more than once in the list. Nothing was saved.',
  },
  REVENUE_CLASS_REQUEST_EMPTY: {
    ptBR: 'Informe confirmAllSuggestions: true e/ou uma lista classes com accountId e revenueClass.',
    en: 'Pass confirmAllSuggestions: true and/or a classes list with accountId and revenueClass.',
  },
  REVENUE_TAX_RATE_OUT_OF_RANGE: {
    ptBR: 'A alíquota {value} equivale a {percent} %, acima de 25 %: parece erro de digitação. Na tela, digite o percentual (1,65 para 1,65 %). Na API, a alíquota é uma fração: digite 0.0165 para 1,65 %.',
    en: 'The rate {value} is {percent} %, above 25 %: it looks like a typing mistake. On screen, type the percent (1.65 for 1.65 %). In the API the rate is a fraction: type 0.0165 for 1.65 %.',
  },
  REVENUE_TAX_RULE_LEGAL_BASIS_REQUIRED: {
    ptBR: 'Toda regra de tributo sobre a receita precisa dizer o porquê: informe a base legal ou a justificativa (legalBasis).',
    en: 'Every Revenue Tax rule must say why: pass the legal basis or the reason (legalBasis).',
  },
  REVENUE_TAX_RULE_INVALID: REVENUE_TAX_ERROR_MESSAGES.REVENUE_TAX_RULE_INVALID,
  REVENUE_TAX_RULE_PERIOD_INVALID: {
    ptBR: 'A vigência da regra é inválida: use datas AAAA-MM-DD e o fim não pode ser anterior ao início.',
    en: 'The rule validity is invalid: use YYYY-MM-DD dates and the end cannot be before the start.',
  },
  REVENUE_TAX_RULE_NOT_FOUND: {
    ptBR: 'Regra de tributo sobre a receita não encontrada.',
    en: 'Revenue Tax rule not found.',
  },
  REVENUE_TAX_RULE_ENTITY_NOT_FOUND: {
    ptBR: 'A entidade informada para a regra não existe.',
    en: 'The entity named for the rule does not exist.',
  },
  REVENUE_TAX_RULE_COUNTRY_MISMATCH: {
    ptBR: 'A entidade {entity} é de outro país ({received}); a regra é do país {expected}. Escolha só entidades desse país ou troque o país da regra. Nada foi salvo.',
    en: 'Entity {entity} is in another country ({received}); the rule is for country {expected}. Pick only entities of that country or change the rule country. Nothing was saved.',
  },
  REVENUE_TAX_RULE_OVERLAP: REVENUE_TAX_ERROR_MESSAGES.REVENUE_TAX_RULE_OVERLAP,
  // Warnings.
  // The basis moves to CASH (Lucro Real -> Lucro Presumido). The move out of the cash basis has its
  // own text (decision I29), picked in `renderRevenueTaxSetupMessage`.
  REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME: {
    ptBR: 'O regime de reconhecimento das receitas de PIS/COFINS muda de {fromBasis} para {toBasis} a partir de agora: as próximas receitas seguem o novo regime. Confirme com seu contador como tratar o PIS/COFINS da receita já provisionada e ainda não recebida.',
    en: 'The PIS/COFINS revenue recognition basis changes from {fromBasis} to {toBasis} from now on: the next revenue follows the new basis. Confirm with your accountant how to treat the PIS/COFINS of revenue already provisioned and not yet received.',
  },
  REVENUE_TAX_REGIME_CHANGES_WITH_POSTED_PROVISIONS: {
    ptBR: 'As provisões já lançadas não mudam. Receita com data anterior à mudança e lançada depois usa as novas alíquotas.',
    en: 'Posted provisions do not change. Revenue dated before the change and posted after it uses the new rates.',
  },
  REVENUE_CLASS_CHANGED_WITH_POSTED_PROVISIONS: {
    ptBR: 'A conta {account} já tem provisões lançadas. A nova classe vale apenas para as próximas receitas; as provisões existentes não mudam.',
    en: 'Account {account} already has posted provisions. The new class applies to future revenue only; existing provisions do not change.',
  },
  REVENUE_CLASS_SHARED_CHART: {
    ptBR: 'O plano de contas é compartilhado com outras entidades: a classe vale para todas elas.',
    en: 'The chart is shared with other entities: the class applies to all of them.',
  },
});

/** pt-BR / en names of the entity-level fields, for screens and the MCP tools. */
export const REVENUE_TAX_FIELD_LABELS = Object.freeze({
  revenueClass: { ptBR: 'Classe de receita', en: 'Revenue Class' },
  revenueTaxStartMonth: {
    ptBR: 'Mês de início dos tributos sobre a receita',
    en: 'Revenue Tax start month',
  },
  revenueTaxDueBasis: {
    ptBR: 'Regime de reconhecimento das receitas (competência ou caixa)',
    en: 'Revenue recognition basis (accrual or cash)',
  },
  revenueTaxRule: { ptBR: 'Regra de tributo sobre a receita', en: 'Revenue Tax rule' },
} satisfies Record<string, RevenueTaxCopy>);

const DUE_BASIS_WORDS: Readonly<Record<string, RevenueTaxCopy>> = {
  CASH: { ptBR: 'caixa', en: 'cash' },
  ACCRUAL: { ptBR: 'competência', en: 'accrual' },
};

/**
 * Leaving the cash basis (Lucro Presumido on cash -> Lucro Real), decision I29. The system keeps
 * the deferred tax in "a diferir" until the receipt, but for income tax the revenue not yet
 * received enters the base of the period before the change (IN RFB 1.700/2017 art. 223-A), and
 * IN RFB 2.121/2022 art. 53 ties the PIS/COFINS cash basis to the IRPJ one: the accountant decides.
 * The amount is named only when the API sends `deferredAmount`.
 */
const DUE_BASIS_LEAVES_CASH_COPY: RevenueTaxCopy = {
  ptBR: 'O regime de reconhecimento das receitas de PIS/COFINS muda de {fromBasis} para {toBasis} a partir de agora. O sistema mantém o PIS/COFINS já diferido em "a diferir" e só o move no recebimento. Pela IN RFB 1.700/2017, art. 223-A, a receita ainda não recebida entra na base do período anterior à mudança. Confirme com seu contador como tratar o PIS/COFINS diferido.',
  en: 'The PIS/COFINS revenue recognition basis changes from {fromBasis} to {toBasis} from now on. The system keeps the tax already deferred in "to defer" and only moves it at receipt. Under IN RFB 1.700/2017 art. 223-A, revenue not yet received enters the base of the period before the change. Confirm with your accountant how to treat the deferred PIS/COFINS.',
};
const DUE_BASIS_LEAVES_CASH_WITH_AMOUNT_COPY: RevenueTaxCopy = {
  ptBR: 'O regime de reconhecimento das receitas de PIS/COFINS muda de {fromBasis} para {toBasis} a partir de agora. O sistema mantém {deferredAmount} em "a diferir" e só os move no recebimento. Pela IN RFB 1.700/2017, art. 223-A, a receita ainda não recebida entra na base do período anterior à mudança. Confirme com seu contador como tratar o PIS/COFINS diferido.',
  en: 'The PIS/COFINS revenue recognition basis changes from {fromBasis} to {toBasis} from now on. The system keeps {deferredAmount} in "to defer" and only moves it at receipt. Under IN RFB 1.700/2017 art. 223-A, revenue not yet received enters the base of the period before the change. Confirm with your accountant how to treat the deferred PIS/COFINS.',
};

/** A fraction written as a percent for a message ("0.3" -> "30", "0.0165" -> "1,65"), or null. */
function fractionAsPercentText(value: unknown, locale: RevenueTaxSetupLocale): string | null {
  const fraction = Number(value);
  if (!Number.isFinite(fraction)) return null;
  const text = String(Number((fraction * 100).toFixed(4)));
  return locale === 'ptBR' ? text.replace('.', ',') : text;
}

function formatDeferredAmount(value: unknown, locale: RevenueTaxSetupLocale): string | null {
  const amount = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(amount)) {
    return null;
  }
  return new Intl.NumberFormat(locale === 'ptBR' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

function describeAccount(value: unknown): string {
  if (value && typeof value === 'object') {
    const row = value as { code?: unknown; name?: unknown };
    return [row.code, row.name].filter((part) => typeof part === 'string' && part).join(' ');
  }
  return String(value ?? '');
}

/**
 * Fills the `{placeholders}` of a code's copy from its params. Known params:
 * `count`, `accounts` (list of `{code,name}`, with `count` = the full total), `account`,
 * `slotCode`, `reason`, `months` (list of `YYYY-MM`), `startMonth`, `value`, `versionLabel`,
 * `fromBasis` / `toBasis` (`CASH` | `ACCRUAL`), `deferredAmount` (BRL, optional, only on
 * the due-basis warning when the API sends it).
 * `entityName`, `validFrom` (the rule an overlap clashes with), `validUntil` (the current end of a
 * rule an end date would extend): the two days are shown as dd/mm/yyyy in pt-BR.
 */
export function renderRevenueTaxSetupMessage(
  code: keyof typeof REVENUE_TAX_SETUP_MESSAGES,
  params: Record<string, unknown>,
  locale: RevenueTaxSetupLocale,
): string {
  let template = REVENUE_TAX_SETUP_MESSAGES[code][locale];
  if (code === 'REVENUE_TAX_NOT_APPLICABLE') {
    const reason = params.reason as RevenueTaxNotApplicableReason | undefined;
    template = reason ? REVENUE_TAX_SETUP_NOT_APPLICABLE_COPY[reason][locale] : template;
  }
  if (code === 'REVENUE_TAX_RULE_INVALID' && typeof params.reason === 'string') {
    template = REVENUE_TAX_RULE_INVALID_REASON_COPY[params.reason]?.[locale] ?? template;
  }
  const hasEntityName = typeof params.entityName === 'string' && params.entityName.length > 0;
  const overlapNamesStart =
    code === 'REVENUE_TAX_RULE_OVERLAP' && typeof params.validFrom === 'string';
  if (overlapNamesStart) {
    // The refusal says which rule it clashes with (since when) and whose it is.
    template = REVENUE_TAX_RULE_OVERLAP_WITH_START_COPY[locale];
  } else if (code === 'REVENUE_TAX_RULE_OVERLAP' && hasEntityName) {
    // A create call with several entities refuses as a whole: the text names the entity.
    template += locale === 'ptBR' ? ' Entidade: {entityName}.' : ' Entity: {entityName}.';
  }
  const deferredAmount =
    code === 'REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME'
      ? formatDeferredAmount(params.deferredAmount, locale)
      : null;
  if (code === 'REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME' && params.fromBasis === 'CASH') {
    template = (
      deferredAmount ? DUE_BASIS_LEAVES_CASH_WITH_AMOUNT_COPY : DUE_BASIS_LEAVES_CASH_COPY
    )[locale];
  }
  if (
    code === 'REVENUE_TAX_CHART_VERSION_NOT_READY' &&
    !(Array.isArray(params.months) && params.months.length > 0)
  ) {
    const named = typeof params.versionLabel === 'string' && params.versionLabel.length > 0;
    template = (
      named
        ? REVENUE_TAX_CHART_VERSION_NOT_READY_NO_MONTH_COPY
        : REVENUE_TAX_CHART_VERSION_NOT_READY_NO_PARAMS_COPY
    )[locale];
  }
  // A blocker found on a chart version names it, so the person knows which chart to fix. So does
  // the posted-provisions warning on a class copied to an older version.
  if (
    typeof params.versionLabel === 'string' &&
    params.versionLabel &&
    ((code in RevenueTaxSetupBlockerCode &&
      code !== 'REVENUE_TAX_NOT_APPLICABLE' &&
      code !== 'REVENUE_TAX_NO_ACTIVE_CHART') ||
      code === 'REVENUE_CLASS_CHANGED_WITH_POSTED_PROVISIONS')
  ) {
    template +=
      locale === 'ptBR' ? ' Plano de contas: {versionLabel}.' : ' Chart version: {versionLabel}.';
  }
  const vars: Record<string, string> = {};
  if (overlapNamesStart) {
    vars.who = hasEntityName ? String(params.entityName) : ORGANIZATION_WORDS[locale];
  }
  if (code === 'REVENUE_TAX_RULE_COUNTRY_MISMATCH') {
    vars.entity = String(params.entityName ?? params.entityId ?? '');
  }
  if (code === 'REVENUE_TAX_RATE_OUT_OF_RANGE') {
    vars.percent = fractionAsPercentText(params.value, locale) ?? '';
  }
  for (const [key, value] of Object.entries(params)) {
    if (key === 'fromBasis' || key === 'toBasis') {
      vars[key] = DUE_BASIS_WORDS[String(value)]?.[locale] ?? String(value ?? '');
    } else if (key === 'accounts' && Array.isArray(value)) {
      const shown = value.map(describeAccount).join('; ');
      const total = typeof params.count === 'number' ? params.count : value.length;
      const more = total - value.length;
      vars.accounts =
        more > 0 ? `${shown}; ${locale === 'ptBR' ? `e mais ${more}` : `and ${more} more`}` : shown;
    } else if (key === 'validFrom' || key === 'validUntil') {
      vars[key] = formatDayParam(value, locale);
    } else if (key === 'account') {
      vars.account = describeAccount(value);
    } else if (Array.isArray(value)) {
      vars[key] = value.map(String).join(', ');
    } else {
      vars[key] = String(value ?? '');
    }
  }
  if (deferredAmount) vars.deferredAmount = deferredAmount;
  if (typeof params.slotCode === 'string') {
    vars.slot = REVENUE_TAX_SLOT_LABELS[params.slotCode]?.[locale] ?? params.slotCode;
  }
  return template.replace(/\{(\w+)\}/g, (_, key: string) => vars[key] ?? '');
}

// ---------------------------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------------------------

interface BlockerBase {
  /** English copy, rendered from the params. */
  message: string;
  /** pt-BR copy, rendered from the params. */
  messagePtBR: string;
  /**
   * The chart version the blocker was found on (the classes, slot mappings and rails of EVERY
   * chart version in play are checked, not only the current one). Absent on entity-level blockers.
   */
  versionId?: string;
  versionLabel?: string;
}

export type RevenueTaxSetupBlocker =
  | (BlockerBase & {
      code: typeof RevenueTaxSetupBlockerCode.REVENUE_ACCOUNTS_WITHOUT_CLASS;
      params: {
        /** Total accounts without a class. */
        count: number;
        /** The first five. */
        accounts: { accountId: string; code: string; name: string }[];
      };
    })
  | (BlockerBase & {
      code: typeof RevenueTaxSetupBlockerCode.REVENUE_TAX_ACCOUNT_NOT_MAPPED;
      params: { slotCode: string; role: RevenueTaxSlotRole };
    })
  | (BlockerBase & {
      code: typeof RevenueTaxSetupBlockerCode.REVENUE_TAX_RAIL_NOT_ENSURED;
      params: { slotCode: string; role: RevenueTaxSlotRole };
    })
  | (BlockerBase & {
      code: typeof RevenueTaxSetupBlockerCode.REVENUE_TAX_NOT_APPLICABLE;
      params: { reason: RevenueTaxNotApplicableReason };
    })
  | (BlockerBase & {
      code: typeof RevenueTaxSetupBlockerCode.REVENUE_TAX_NO_ACTIVE_CHART;
      params: Record<string, never>;
    });

/** What an account slot does for Revenue Tax. */
export type RevenueTaxSlotRole = 'DEDUCTION' | 'LIABILITY' | 'DEFERRED_LIABILITY' | 'PREPAID_ASSET';

export interface RevenueTaxSetupWarning {
  code: RevenueTaxSetupWarningCode;
  params: Record<string, unknown>;
  message: string;
  messagePtBR: string;
  /**
   * Only on the warnings of the setup status. `BLOCKS_START`: the start month is refused (and
   * `readyToStart` is false). `NEEDS_ACKNOWLEDGEMENT`: the start month is accepted only with
   * `acknowledgeWarnings: true` (warn + consent; `readyToStart` is not affected).
   */
  effect?: 'BLOCKS_START' | 'NEEDS_ACKNOWLEDGEMENT';
}

export interface RevenueTaxSetupAccountRow {
  accountId: string;
  code: string;
  name: string;
  /** Names of the ancestors, nearest first (display and suggestion context). */
  parentNames: string[];
  /** Current class on the active chart version, or null. */
  revenueClass: RevenueClass | null;
  suggestedRevenueClass: RevenueClass;
  suggestionReason: RevenueClassSuggestionReason;
  /** The words that matched, or null on the default. */
  suggestionMatchedTerm: string | null;
  /** True on an operating suggestion in a NON_CUMULATIVE entity: ask the user (Lei 10.833/2003 art. 10). */
  needsReview: boolean;
  needsReviewReason: RevenueClassReviewReason | null;
  /**
   * True when the suggestion lowers the tax (any class but OPERATING) or came only from an
   * ancestor's name. "Confirm all suggestions" and the chart import never apply it: a person
   * sends it in `classes[]`.
   */
  requiresExplicitConfirmation: boolean;
  requiresExplicitConfirmationReason: RevenueClassExplicitConfirmationReason | null;
  /** Posted provisions already exist for this account (a class change then warns). */
  hasPostedProvisions: boolean;
}

export interface RevenueTaxSetupMappingRow {
  slotCode: string;
  role: RevenueTaxSlotRole;
  /** The tax this slot belongs to. */
  taxSlotCode: RevenueTaxSlotCode;
  /** Always required, or required because the due basis is CASH. */
  requiredBecause: 'ALWAYS' | 'CASH_BASIS';
  label: RevenueTaxCopy;
  mapped: boolean;
  accountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  /** The staged-liability rail (Apurado / Em guias) exists; null for slots that have no rail. */
  railEnsured: boolean | null;
}

export interface RevenueTaxSetupRuleRow {
  /** The country of the rule in force (the entity's country, I58). */
  countryCode: string;
  revenueClass: RevenueClass;
  taxSlotCode: RevenueTaxSlotCode;
  outcome: RevenueTaxOutcome;
  /** Fraction as a decimal string ("0.0165" = 1,65 %); null unless TAXED. */
  rateFraction: string | null;
  /** `DEFAULT` is the system table (S1 stores it as `SYSTEM_DEFAULT`; this row says `DEFAULT`). */
  source: 'DEFAULT' | 'TENANT_OVERRIDE' | 'ENTITY_OVERRIDE';
  /** The same value in the engine's vocabulary. */
  engineSource: RevenueTaxRuleSource | null;
  ruleId: string | null;
  legalBasis: string | null;
  validFrom: string | null;
  validUntil: string | null;
  /** No rule is in force at `asOf` (the provision would be NOT_APPLICABLE / NO_RULE_AT_DATE). */
  noRuleAtDate: boolean;
}

export interface RevenueTaxSetupChartVersion {
  versionId: string;
  /** `<name> (<version number>)`. */
  label: string;
  /** CURRENT = the active assignment; PERIOD_PINNED = pinned by periods; BRANCH = DRAFT / SCHEDULED child. */
  role: 'CURRENT' | 'PERIOD_PINNED' | 'BRANCH';
  status: string;
  /** No blocker was found on this version. */
  ready: boolean;
  accountSummary: { total: number; classified: number; unclassified: number };
  /**
   * Only on a PERIOD_PINNED version: the first month with no period pinned to a version other than
   * the current one (the earliest start month that leaves this version out), or null.
   */
  earliestStartMonthWithoutIt: string | null;
}

export interface RevenueTaxSetupStatus {
  entityId: string;
  /** The date the rules were resolved at (`YYYY-MM-DD`, UTC). */
  asOf: string;
  entity: {
    countryCode: string;
    companyTaxRegime: CompanyTaxRegime | null;
    pisCofinsRegime: PisCofinsRegime | null;
    /** `YYYY-MM-01` or null = Revenue Tax is off for the entity. */
    revenueTaxStartMonth: string | null;
    /** What is stored. */
    revenueTaxDueBasis: TaxDueBasis;
    /** What applies: CASH only counts under Lucro Presumido (MP 2.158-35/2001 art. 20). */
    effectiveDueBasis: TaxDueBasis;
  };
  applicable: boolean;
  notApplicableReason: RevenueTaxNotApplicableReason | null;
  /** The entity's active chart (null when it has none). */
  chart: { coaVersionId: string; bookId: string; sharedWithOtherEntities: boolean } | null;
  /**
   * The month the chart versions in play were looked up for: the candidate month of the request,
   * else the stored start month, else null (then only the current version and its branches).
   */
  evaluatedFromMonth: string | null;
  /**
   * Every chart version the engine can read classes and mappings from for this entity: the current
   * assignment, the versions its periods from the month on are pinned to, and DRAFT / SCHEDULED
   * branches of those. `readyToStart` needs all of them ready.
   */
  chartVersions: RevenueTaxSetupChartVersion[];
  accountSummary: {
    total: number;
    classified: number;
    unclassified: number;
    needsReview: number;
  };
  revenueAccounts: RevenueTaxSetupAccountRow[];
  mappings: RevenueTaxSetupMappingRow[];
  rules: RevenueTaxSetupRuleRow[];
  blockers: RevenueTaxSetupBlocker[];
  /** Non-blocker notices: see `effect` on each one. Empty when nothing needs attention. */
  warnings: RevenueTaxSetupWarning[];
  /** Applicable, no blockers and no BLOCKS_START warning: the start month can be set. */
  readyToStart: boolean;
  /** A start month is saved. */
  started: boolean;
  /** Provisions waiting for setup (status PENDING). */
  pendingProvisionCount: number;
  /** Posted provisions exist (the due basis is locked and the start month cannot move later). */
  hasPostedProvisions: boolean;
}

export interface SetRevenueClassesRequest {
  /**
   * Give every unclassified revenue account whose suggestion does NOT need explicit confirmation
   * its suggested class (the OPERATING ones). The rest are listed in
   * `skippedForExplicitConfirmation` and stay unclassified until sent in `classes`.
   */
  confirmAllSuggestions?: boolean;
  /**
   * Candidate start month (`YYYY-MM-01`): classes are also written to the same-code accounts of
   * the chart versions the entity's periods from that month on are pinned to. Defaults to the
   * stored start month.
   */
  startMonth?: string;
  /** Explicit classes; they win over the suggestions and may change an existing class. */
  classes?: { accountId: string; revenueClass: RevenueClass }[];
}

export interface SetRevenueClassesResponse {
  entityId: string;
  coaVersionId: string;
  updated: {
    accountId: string;
    code: string;
    name: string;
    previousClass: RevenueClass | null;
    revenueClass: RevenueClass;
    source: 'EXPLICIT' | 'SUGGESTION';
  }[];
  /** Accounts that already had the requested class. */
  unchangedCount: number;
  /**
   * Unclassified accounts "confirm all" left alone because their suggestion needs a person
   * (lowers the tax, or matched only an ancestor's name). Send them in `classes`.
   */
  skippedForExplicitConfirmation: {
    accountId: string;
    code: string;
    name: string;
    suggestedRevenueClass: RevenueClass;
    reason: RevenueClassExplicitConfirmationReason;
    message: string;
    messagePtBR: string;
  }[];
  /**
   * The same classes written to the same-code accounts of the OTHER chart versions in play.
   * OPERATING is copied by code; every other class only when the account names match
   * (decision I20), see `notPropagated`.
   */
  otherVersions: {
    versionId: string;
    versionLabel: string;
    /** Accounts updated on that version (`updated.length`). */
    updatedCount: number;
    /** The accounts updated on that version. */
    updated: {
      accountId: string;
      code: string;
      name: string;
      previousClass: RevenueClass | null;
      revenueClass: RevenueClass;
    }[];
    /** Codes of the changed accounts that are missing, or not income accounts, on that version. */
    missingCodes: string[];
  }[];
  /**
   * The classes NOT copied to another chart version because they lower the tax and the account of
   * that version has a different name (decision I20). The account keeps its class (or none, and the
   * version stays blocked until a person classifies it); classify it on that version.
   */
  notPropagated: {
    versionId: string;
    versionLabel: string;
    /** The account of the other version: where the class has to be set. */
    targetAccountId: string;
    code: string;
    /** The name of the account on the current chart, where the class was set. */
    sourceName: string;
    /** The name of the same-code account on the other version. */
    targetName: string;
    /** The class that was not copied. */
    revenueClass: RevenueClass;
  }[];
  warnings: RevenueTaxSetupWarning[];
  status: RevenueTaxSetupStatus;
}

export interface EnsureRevenueTaxAccountsSlotResult {
  slotCode: string;
  role: RevenueTaxSlotRole;
  /** REUSED = already mapped; CREATED = account created and mapped now; NOT_RESOLVED = no anchor to create next to. */
  outcome: 'REUSED' | 'CREATED' | 'NOT_RESOLVED';
  /**
   * The account the slot resolves to after this call. For a liability slot that is the rail's
   * managed parent (the account created for the slot becomes its "- Legado" child).
   */
  accountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  /** Only on NOT_RESOLVED: why. */
  reasonCode: RevenueTaxEnsureReasonCode | null;
  reason: string | null;
  reasonPtBR: string | null;
}

export interface EnsureRevenueTaxAccountsRailResult {
  slotCode: string;
  outcome: 'REUSED' | 'CREATED' | 'NOT_ENSURED';
  railId: string | null;
}

export interface EnsureRevenueTaxAccountsResponse {
  entityId: string;
  /** The entity's current chart version; `slots` and `rails` are for it. */
  coaVersionId: string;
  dueBasis: TaxDueBasis;
  slots: EnsureRevenueTaxAccountsSlotResult[];
  rails: EnsureRevenueTaxAccountsRailResult[];
  /** The other chart versions in play, same shape (a slot that cannot be created is NOT_RESOLVED). */
  otherVersions: {
    versionId: string;
    versionLabel: string;
    slots: EnsureRevenueTaxAccountsSlotResult[];
    rails: EnsureRevenueTaxAccountsRailResult[];
  }[];
  status: RevenueTaxSetupStatus;
}

export interface ConfigureRevenueTaxRequest {
  /** `YYYY-MM-01` (or `YYYY-MM`). `null` clears it. Omit to leave it unchanged. */
  revenueTaxStartMonth?: string | null;
  revenueTaxDueBasis?: TaxDueBasis;
  /** The entity also uses the cash basis for IRPJ/CSLL. Required for CASH (MP 2.158-35/2001 art. 20). */
  cashBasisAlsoForIncomeTax?: boolean;
  /**
   * Consent to the NEEDS_ACKNOWLEDGEMENT warnings of the status when switching Revenue Tax on
   * (setting a start month where there was none), to a regime change that flips the effective
   * due basis over posted provisions (`REVENUE_TAX_DUE_BASIS_CHANGES_WITH_REGIME`), and to any
   * regime change over posted provisions (`REVENUE_TAX_REGIME_CHANGES_WITH_POSTED_PROVISIONS`).
   * Without it the call is refused with `REVENUE_TAX_WARNINGS_NOT_ACKNOWLEDGED` and nothing is saved.
   */
  acknowledgeWarnings?: boolean;
}

export interface ConfigureRevenueTaxResponse {
  entityId: string;
  revenueTaxStartMonth: string | null;
  revenueTaxDueBasis: TaxDueBasis;
  status: RevenueTaxSetupStatus;
}

export interface RevenueTaxRuleOverrideResponse {
  ruleId: string;
  /** null = tenant-wide. */
  entityId: string | null;
  /** The entity's name (a rule of one entity); null for a tenant-wide rule. */
  entityName?: string | null;
  scope: 'TENANT' | 'ENTITY';
  /** ISO 3166-1 alpha-2 country the rule belongs to (I58). */
  countryCode: string;
  pisCofinsRegime: PisCofinsRegime;
  revenueClass: RevenueClass;
  taxSlotCode: RevenueTaxSlotCode;
  outcome: RevenueTaxOutcome;
  rateFraction: string | null;
  validFrom: string;
  validUntil: string | null;
  legalBasis: string | null;
  note: string | null;
  /** Relative to `asOf` of the listing: SCHEDULED, ACTIVE or ENDED. */
  state: 'SCHEDULED' | 'ACTIVE' | 'ENDED';
  createdAt: string;
}

/**
 * The answer of `POST fiscal/revenue-tax/rule-overrides` when the body names `entityIds`: one rule
 * per entity, in the order of the request. The whole call is one transaction (all or nothing).
 */
export interface RevenueTaxRuleOverrideBatchResponse {
  rules: RevenueTaxRuleOverrideResponse[];
}

/**
 * The body of `POST fiscal/revenue-tax/rule-overrides`. Scope: `entityIds` (one rule per entity,
 * all or nothing), or the `entityId` query parameter (one entity; kept for compatibility), or
 * neither for an organization (tenant-wide) rule. `entityId` together with `entityIds` is refused.
 */
export interface UpsertRevenueTaxRuleOverrideRequest {
  /**
   * ISO 3166-1 alpha-2 country of the rule (I58). Required for an organization rule. With
   * entities it defaults to their country and, when given, must equal it
   * (`REVENUE_TAX_RULE_COUNTRY_MISMATCH` otherwise).
   */
  countryCode?: string;
  /** Up to 100 distinct entity ids; one rule is created per entity. */
  entityIds?: string[];
  pisCofinsRegime: PisCofinsRegime;
  revenueClass: RevenueClass;
  taxSlotCode: RevenueTaxSlotCode;
  outcome: RevenueTaxOutcome;
  /**
   * The rate as a FRACTION: 0.0165 for 1.65 %. Required when the outcome is TAXED, empty otherwise.
   * A value above 0.25 is refused as a typed percent (`REVENUE_TAX_RATE_OUT_OF_RANGE`).
   */
  rateFraction?: string | number | null;
  /** `YYYY-MM-DD`, inclusive. */
  validFrom: string;
  /** `YYYY-MM-DD`, inclusive; omit for open-ended. */
  validUntil?: string | null;
  /** Why this override exists (required). */
  legalBasis: string;
  note?: string | null;
}

export interface EndRevenueTaxRuleOverrideRequest {
  /** `YYYY-MM-DD`, inclusive. */
  validUntil: string;
}
